"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineSteps = [{
  title: "SYSTEM INITIALIZATION",
  description: "Deploy the v-iral kernel across target devices to establish the primary gatekeeper protocol and parent-child synchronization."
}, {
  title: "NEURAL MAPPING",
  description: "Our AI engine analyzes the child's academic curriculum and personal interest vectors to generate a customized learning matrix."
}, {
  title: "APP ENCAPSULATION",
  description: "Social media and entertainment platforms are locked within a secure containment field, accessible only via knowledge verification."
}, {
  title: "KNOWLEDGE CHALLENGES",
  description: "The protocol deploys micro-quizzes and educational modules that must be solved in real-time to unlock temporary application access."
}, {
  title: "PROGRESS TELEMETRY",
  description: "Encrypted data packets transmit learning metrics and cognitive growth analytics directly to the parental control interface."
}, {
  title: "ADAPTIVE SCALING",
  description: "The gatekeeper dynamically adjusts challenge complexity in real-time, ensuring the educational barrier evolves with the child's intelligence."
}, {
  title: "DIGITAL SOVEREIGNTY",
  description: "Consistent performance unlocks higher-tier rewards and autonomous browsing windows, incentivizing long-term intellectual growth."
}];
function TimelineItem({
  step,
  index,
  title,
  description
}: {
  step: number;
  index: number;
  title: string;
  description: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const formattedStep = step < 10 ? `0${step}` : step;
  return <div data-section-id="147905" data-index={index} ref={ref} className={`relative flex w-full flex-col md:flex-row items-center mb-16 last:mb-0 ${isVisible ? "opacity-100" : "opacity-0"} transition-opacity duration-700 ease-out`}>
      {}
      <div className={`absolute left-4 md:left-1/2 w-5 h-5 rounded-none bg-background border-4 border-primary -translate-x-1/2 z-20 transition-all duration-500 delay-300 ${isVisible ? "scale-100 opacity-100" : "scale-0 opacity-0"} top-6 md:top-1/2 md:-translate-y-1/2 shadow-[0_0_20px_rgba(120,255,0,0.8)]`} />

      {}
      <div className={`w-full pl-12 md:pl-0 md:w-1/2 flex ${isEven ? "md:justify-end md:pr-16" : "md:ml-auto md:justify-start md:pl-16"}`}>
        <Card className={`w-full max-w-md bg-card/90 backdrop-blur-sm border-2 border-primary/20 rounded-none shadow-none transition-all duration-700 ease-out transform ${isVisible ? "translate-y-0" : "translate-y-[20px]"} hover:border-primary/60 hover:shadow-[0_0_15px_rgba(120,255,0,0.2)]`}>
          <CardHeader className="pb-4">
            <Badge variant="default" className="w-fit mb-3 text-5xl md:text-6xl font-default text-primary bg-transparent border-none p-0 tracking-tighter leading-none">
              {formattedStep}
            </Badge>
            <CardTitle className="text-xl md:text-2xl leading-tight text-foreground font-default uppercase tracking-widest">
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground font-mono text-sm leading-relaxed">
              {description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheBlueprint() {
  const sectionRef = useRef<HTMLElement>(null);
  const [fillHeight, setFillHeight] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const startPoint = windowHeight / 2;
      let progress = (startPoint - sectionTop) / sectionHeight;
      progress = Math.max(0, Math.min(1, progress));
      setFillHeight(progress * 100);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return;
}